import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiCheck, FiCreditCard, FiShield, FiDollarSign, FiSearch } from 'react-icons/fi';
import paymentService from '../services/payment.service';
import memberService from '../services/member.service';
import membershipService from '../services/membership.service';
import PaymentWaitingModal from '../components/PaymentWaitingModal';
import DateInput from '../components/DateInput';

const MembershipPurchase = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { plan, memberId, isUpgrade, currentMembershipId, currentPlanPrice, currentPlanName } = location.state || {};
  const hasFetched = useRef(false);

  console.log('=== MembershipPurchase Component Mount ===');
  console.log('Location State:', location.state);
  console.log('Plan:', plan);
  console.log('Member ID:', memberId);
  console.log('Is Upgrade:', isUpgrade);
  console.log('Current Membership ID:', currentMembershipId);
  console.log('Current Plan Price:', currentPlanPrice);
  console.log('Current Plan Name:', currentPlanName);

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(memberId ? memberId.toString() : '');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);

  useEffect(() => {
    if (!plan) {
      toast.error('No plan selected');
      navigate('/membership/plans');
      return;
    }

    // Prevent duplicate fetches (even with StrictMode)
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount


  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await memberService.getAllMembers({ limit: 1000 });
      // Filter to show only members without active plans AND without pending payments
      const allMembers = response.data?.members || [];
      const membersWithoutPlans = allMembers.filter(
        member => {
          // Always include the pre-selected member even if they have an active plan
          if (memberId && member.id === parseInt(memberId)) {
            return true;
          }
          // Filter out members with active plans OR pending payments
          return (!member.membership_status || member.membership_status !== 'active') && !member.has_pending_payment;
        }
      );
      setMembers(membersWithoutPlans);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const handleCashPayment = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member');
      return;
    }

    try {
      setPaymentProcessing(true);

      let orderResponse;

      if (isUpgrade) {
        // Create upgrade order
        orderResponse = await membershipService.upgradeMembership(selectedMemberId, {
          new_plan_id: plan.id,
          payment_method: 'cash'
        });
      } else {
        // Create regular membership order
        orderResponse = await paymentService.createMembershipOrder({
          member_id: selectedMemberId,
          plan_id: plan.id,
          payment_method: 'cash',
          start_date: startDate || null
        });
      }

      if (!orderResponse.success) {
        throw new Error('Failed to create order');
      }

      const { invoice_id } = orderResponse.data;

      // For cash, directly confirm payment (mark as paid)
      const confirmResponse = await paymentService.confirmCashPayment({
        invoice_id: invoice_id
      });

      if (confirmResponse.success) {
        const successMessage = isUpgrade
          ? 'Cash payment recorded! Membership upgraded'
          : 'Cash payment recorded! Membership activated';
        toast.success(successMessage);
        navigate(`/invoice/${invoice_id}`);
      } else {
        toast.error('Payment recording failed');
      }
    } catch (error) {
      console.error('Cash payment error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to process cash payment';
      toast.error(errorMessage);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member');
      return;
    }

    try {
      setPaymentProcessing(true);

      let orderResponse;

      if (isUpgrade) {
        // Create upgrade order
        orderResponse = await membershipService.upgradeMembership(selectedMemberId, {
          new_plan_id: plan.id,
          payment_method: 'online'
        });
      } else {
        // Create regular membership order
        orderResponse = await paymentService.createMembershipOrder({
          member_id: selectedMemberId,
          plan_id: plan.id,
          payment_method: 'online',
          start_date: startDate || null
        });
      }

      if (!orderResponse.success) {
        throw new Error('Failed to create order');
      }

      const { invoice_id, order } = orderResponse.data;

      // Get member info
      const memberResponse = await memberService.getMemberById(selectedMemberId);
      const member = memberResponse.data;

      // Show modal instead of redirect
      const paymentAmount = isUpgrade && orderResponse.data.upgrade_cost
        ? orderResponse.data.upgrade_cost
        : plan.final_price;

      setPendingPayment({
        invoiceId: invoice_id,
        paymentLink: order.payment_link,
        expiresAt: order.expires_at,
        amount: paymentAmount,
        memberName: `${member.first_name} ${member.last_name}`
      });
      setShowPaymentModal(true);

      // Still show toasts for admin reference
      const linkMessage = isUpgrade
        ? `Upgrade payment link sent to ${member.phone}${member.email ? ' and ' + member.email : ''}!`
        : `Payment link sent to ${member.phone}${member.email ? ' and ' + member.email : ''}!`;
      toast.success(linkMessage);
    } catch (error) {
      console.error('Payment link error:', error);

      // Handle duplicate payment conflict (409)
      if (error.response?.status === 409) {
        const existingInvoiceId = error.response?.data?.data?.invoice_id;
        if (existingInvoiceId) {
          toast.info('Payment already in progress. Showing status...');
          // TODO: Could fetch existing invoice details and show modal
          // For now, just show the error message
          toast.error(error.response?.data?.message || 'Payment already in progress');
        } else {
          toast.error(error.response?.data?.message || 'Payment already in progress');
        }
      } else if (error.response?.status === 429) {
        // Handle rate limiting (429)
        toast.error(error.response?.data?.message || 'Please wait before retrying');
      } else {
        toast.error('Failed to send payment link');
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'cash') {
      handleCashPayment();
    } else if (paymentMethod === 'online') {
      handleOnlinePayment();
    }
  };

  if (!plan) {
    return null;
  }

  // Filter members based on search term
  const filteredMembers = members.filter(member => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
    const memberId = (member.member_id || '').toLowerCase();
    const phone = (member.phone || '').toLowerCase();

    return fullName.includes(searchLower) ||
           memberId.includes(searchLower) ||
           phone.includes(searchLower);
  });

  const selectedMember = members.find(m => m.id === parseInt(selectedMemberId));

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/membership/plans')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Plans
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Member Selection */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {isUpgrade ? 'Member' : 'Select Member'}
            </h2>
            {isUpgrade ? (
              <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800 font-medium">
                  🔒 Member is locked for upgrade - Cannot be changed
                </p>
              </div>
            ) : memberId ? (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Member has been pre-selected. You can change the selection below if needed.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                Only showing members without active plans
              </p>
            )}

            {/* Search Input */}
            {!isUpgrade && (
              <div className="relative mb-4">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, member ID, or phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : isUpgrade ? (
              // Show selected member only (locked)
              <div className="space-y-3">
                {selectedMember && (
                  <div className="flex items-center p-4 border-2 border-purple-600 bg-purple-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {selectedMember.first_name} {selectedMember.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedMember.member_id} | {selectedMember.phone}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredMembers.map((member) => (
                  <label
                    key={member.id}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedMemberId === member.id.toString()
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="member"
                      value={member.id}
                      checked={selectedMemberId === member.id.toString()}
                      onChange={(e) => setSelectedMemberId(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {member.member_id} | {member.phone}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {!loading && filteredMembers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? (
                  <>
                    <p>No members found matching "{searchTerm}"</p>
                    <button
                      onClick={() => setSearchInput('')}
                      className="mt-4 text-blue-600 hover:text-blue-700"
                    >
                      Clear Search
                    </button>
                  </>
                ) : members.length === 0 ? (
                  <>
                    <p>No members without active plans</p>
                    <p className="text-sm mt-2">All members already have active memberships</p>
                  </>
                ) : (
                  <p>No members found</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Plan Details & Payment */}
        <div className="space-y-6">
          {/* Plan Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {isUpgrade ? 'Upgrade Plan Summary' : 'Plan Summary'}
            </h2>

            {isUpgrade && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-purple-800 font-medium">
                  🔄 Membership Upgrade - You'll only pay the difference between plans
                </p>
              </div>
            )}

            <div className={`bg-gradient-to-r ${isUpgrade ? 'from-purple-500 to-purple-600' : 'from-blue-500 to-blue-600'} rounded-lg p-6 text-white mb-4`}>
              <h3 className="text-2xl font-bold">{plan.name}</h3>
              <p className={`${isUpgrade ? 'text-purple-100' : 'text-blue-100'} mt-1`}>{plan.duration_days} days</p>
            </div>

            {plan.features && plan.features.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="font-semibold text-gray-700">Features:</p>
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <FiCheck className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Price Breakdown */}
            <div className="border-t pt-4 space-y-2">
              {isUpgrade ? (
                // Upgrade pricing breakdown
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>New Plan Price</span>
                    <span>₹{parseFloat(plan.final_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Previous Plan Credit</span>
                    <span>-₹{parseFloat(currentPlanPrice || 0).toFixed(2)}</span>
                  </div>
                  {currentPlanName && (
                    <div className="text-xs text-gray-500 italic">
                      Credit from: {currentPlanName}
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold text-purple-600 border-t pt-2">
                    <span>Upgrade Cost</span>
                    <span>₹{(parseFloat(plan.final_price) - parseFloat(currentPlanPrice || 0)).toFixed(2)}</span>
                  </div>
                </>
              ) : (
                // Regular purchase pricing breakdown
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Base Price</span>
                    <span>₹{parseFloat(plan.price).toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between ${plan.discount_percent > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span>Discount ({plan.discount_percent || 0}%)</span>
                    <span>-₹{(parseFloat(plan.price) - parseFloat(plan.final_price)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2">
                    <span>Total Amount</span>
                    <span>₹{parseFloat(plan.final_price).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Start Date Selection */}
          {!isUpgrade && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Membership Start Date</h3>
              <p className="text-sm text-gray-500 mb-3">
                Select when the membership should begin (defaults to today)
              </p>
              <DateInput
                name="start_date"
                value={startDate}
                onChange={(value) => setStartDate(value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
                required
              />
              <p className="mt-2 text-xs text-gray-500">Enter date as: DD/MM/YYYY (e.g., 28/12/2025)</p>
              {startDate && (
                <p className="text-sm text-gray-600 mt-2">
                  Membership will start on: {new Date(startDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
          )}

          {/* Upgrade Info - Start Date Locked */}
          {isUpgrade && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Membership Start Date</h3>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-medium mb-2">
                  🔒 Start date remains unchanged during upgrade
                </p>
                <p className="text-xs text-purple-600">
                  Your current membership start date will be preserved. Only the plan and end date will be updated based on the new plan's duration.
                </p>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
            <div className="space-y-3 mb-4">
              {/* Cash Payment */}
              <label
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  paymentMethod === 'cash'
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <FiDollarSign className="text-green-600 text-xl mr-3" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Cash Payment</div>
                  <div className="text-sm text-gray-500">Pay directly at the gym counter</div>
                </div>
              </label>

              {/* Online Payment */}
              <label
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  paymentMethod === 'online'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="online"
                  checked={paymentMethod === 'online'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <FiCreditCard className="text-blue-600 text-xl mr-3" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Online Payment (UPI)</div>
                  <div className="text-sm text-gray-500">Payment link via SMS & Email</div>
                </div>
              </label>
            </div>
          </div>

          {/* Payment Method Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <FiShield className="mr-2 text-green-500" />
              {paymentMethod === 'cash' ? 'Cash Payment' : 'Secure Payment'}
            </h3>
            {paymentMethod === 'cash' ? (
              <p className="text-sm text-gray-600 mb-4">
                The payment will be recorded as cash. Please collect the payment at the gym counter.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Razorpay will automatically send a secure payment link to the member's phone via SMS{' '}
                  {selectedMember?.email && 'and email'}. The link expires in 15 minutes. The member can complete the payment using any UPI app.
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">Google Pay</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">PhonePe</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">Paytm</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">Any UPI App</span>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    ⏰ Payment link expires in 15 minutes. A waiting screen will show the countdown timer. After payment, membership will be activated automatically and invoice will be sent to the member.
                  </p>
                </div>
              </>
            )}

            <button
              onClick={handlePayment}
              disabled={!selectedMemberId || paymentProcessing}
              className={`w-full flex items-center justify-center px-6 py-3 text-white rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                paymentMethod === 'cash'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {paymentProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  {paymentMethod === 'cash' ? (
                    <>
                      <FiDollarSign className="mr-2" />
                      Record Cash Payment ₹{parseFloat(plan.final_price).toFixed(2)}
                    </>
                  ) : (
                    <>
                      <FiCreditCard className="mr-2" />
                      Send Payment Link ₹{parseFloat(plan.final_price).toFixed(2)}
                    </>
                  )}
                </>
              )}
            </button>

            {selectedMember && (
              <p className="text-sm text-gray-600 mt-3 text-center">
                Purchasing for: {selectedMember.first_name} {selectedMember.last_name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Payment Waiting Modal */}
      {showPaymentModal && pendingPayment && (
        <PaymentWaitingModal
          invoiceId={pendingPayment.invoiceId}
          paymentLink={pendingPayment.paymentLink}
          expiresAt={pendingPayment.expiresAt}
          memberName={pendingPayment.memberName}
          amount={pendingPayment.amount}
          onSuccess={() => {
            setShowPaymentModal(false);
            toast.success('Payment completed! Redirecting to invoice...');
            setTimeout(() => {
              navigate(`/invoice/${pendingPayment.invoiceId}`);
            }, 1000);
          }}
          onFailure={(reason) => {
            setShowPaymentModal(false);
            toast.error(`Payment ${reason}. Please try again.`);
          }}
          onClose={() => {
            setShowPaymentModal(false);
            setPendingPayment(null);
          }}
        />
      )}
    </div>
  );
};

export default MembershipPurchase;
